/**
 * buildActiveMap — pure function that materialises the fixed board geometry.
 *
 * Player count affects only initial territory distribution. It must not hide
 * continents or change how many territories the selected map contains.
 */

import { MODULE_CONFIG } from './modules.js';
import type { ModuleId, QAModule, Territory, TerritoryId } from './types.js';
import { TERRITORIES, TERRITORY_IDS_BY_MODULE } from './territories.js';

export type PlayerCount = 2 | 3 | 4 | 5 | 6;

export interface ActiveMap {
  /** Player count this map was built for. */
  readonly playerCount: PlayerCount;
  /** All territories on the selected fixed board. */
  readonly territories: readonly Territory[];
  /** Module metadata for the active modules (name, bonus, etc.). */
  readonly modules: readonly QAModule[];
  /**
   * Full-board adjacency lookup. Every key is a territory id; values are
   * arrays of neighbouring territory ids.
   */
  readonly adjacency: ReadonlyMap<TerritoryId, readonly TerritoryId[]>;
}

/** Modules ordered by `revealAtPlayers` so we can slice deterministically. */
const REVEAL_ORDER: readonly ModuleId[] = (() => {
  return [...MODULE_CONFIG]
    .sort((a, b) => a.revealAtPlayers - b.revealAtPlayers || a.id.localeCompare(b.id))
    .map((m) => m.id);
})();

/**
 * Fixed-map mode: every player count uses every module / continent.
 */
export function selectActiveModules(_playerCount: PlayerCount): readonly ModuleId[] {
  return MODULE_CONFIG.map((m) => m.id);
}

/**
 * Build the materialised active map. Pure: same input always yields the same
 * structurally-identical output (referential equality is not promised — call
 * sites should memoise if they care).
 */
export function buildActiveMap(playerCount: PlayerCount): ActiveMap {
  const modules: QAModule[] = MODULE_CONFIG.map((m) => ({
    id: m.id,
    displayName: m.displayName,
    fullControlBonus: m.fullControlBonus,
    revealAtPlayers: m.revealAtPlayers,
    territoryIds: TERRITORY_IDS_BY_MODULE[m.id],
  }));

  const adjacency = new Map<TerritoryId, readonly TerritoryId[]>();
  for (const t of TERRITORIES) {
    adjacency.set(t.id, Object.freeze([...t.adjacencyIds]));
  }

  return {
    playerCount,
    territories: TERRITORIES,
    modules,
    adjacency,
  };
}

export { REVEAL_ORDER };
